import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  addEventExpense, addExpenseUrl, createOppGroup, deleteEventExpense, deleteExpenseLink,
  getEventLeadValues, getPlannerEvents, listEventExpenses, updateOppGroup, uploadExpenseFile,
  type EventExpense, type EventLeadValue, type PlannerEvent,
} from './crmApi'
import { OPP_GROUP_TYPES, STAGE_MAP, oppGroupType } from './crmData'
import {
  CloseButton, ConfirmDialog, CrmToaster, IconCalendarUi, IconLink, IconPaperclip, IconTrash, IconUpload,
  InitialBox, Kpi, Overlay, Pill, SearchInput, SegTabs, crmToast,
} from './crmUi'
import { Button } from '../shared/Button'
import { DealDetail } from './DealDetail'
import { LeadRedirect } from './CrmLeadPage'
import { getDealById } from './crmApi'

/**
 * EVENT PLANNER v2 (Lee, 7 Sep 2026) — MASTER-DETAIL split, per Lee's feedback:
 * the first cut expanded cards in place, which read as a messy pop-up. Now the
 * screen splits: every event as a compact card (or list row) down the LEFT,
 * and ONE permanent detail panel on the RIGHT, organised as tabs —
 * Overview · Expenses · Leads. Selecting an event swaps the panel's content;
 * nothing ever expands, overlays or navigates away.
 *
 * Same events as Shows/Events (one table, wq_crm_opp_groups) — no sync to
 * break. Lead values are ANNUAL estimates: customers at real MRR × 12, everyone
 * else by company-size band anchored on actual paying customers of that size.
 */

const gbp = (n: number) => `£${Math.round(n).toLocaleString()}`
// Defensive: accepts plain yyyy-mm-dd OR a full ISO timestamp — never renders "Invalid date".
const fmtDate = (d: string | null) => {
  if (!d) return null
  const ymd = d.slice(0, 10)
  const parsed = new Date(ymd + 'T00:00:00')
  return isNaN(parsed.getTime()) ? null : parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}
// <input type="date"> only accepts yyyy-mm-dd.
const ymdOf = (d: string | null) => (d ? d.slice(0, 10) : '')
const netTone = (n: number) => (n > 0 ? 'text-emerald-700' : n < 0 ? 'text-red-600' : 'text-txt-secondary')

/** spent vs budget — thin bar, red when over. */
const SpendBar: React.FC<{ spent: number; budget: number | null }> = ({ spent, budget }) => {
  if (!budget || budget <= 0) return null
  const pct = Math.min(100, Math.round((spent / budget) * 100))
  const over = spent > budget
  return (
    <span className="block w-full h-1 rounded-full bg-border overflow-hidden" title={`${gbp(spent)} spent of ${gbp(budget)} expected${over ? ' — OVER BUDGET' : ''}`}>
      <span className={`block h-full rounded-full ${over ? 'bg-red-500' : 'bg-txt-secondary'}`} style={{ width: `${pct}%` }} />
    </span>
  )
}

const DateRange: React.FC<{ ev: PlannerEvent; className?: string }> = ({ ev, className = '' }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] text-txt-tertiary whitespace-nowrap ${className}`}>
    <IconCalendarUi className="w-3 h-3" />
    {fmtDate(ev.date) || 'No date'}{ev.endDate && ev.endDate !== ev.date ? ` → ${fmtDate(ev.endDate)}` : ''}
  </span>
)

// ── inline-editable money (expected cost) ────────────────────────────────────
const InlineMoney: React.FC<{ value: number | null; placeholder: string; onSave: (n: number | null) => void; title: string }> = ({ value, placeholder, onSave, title }) => {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(value != null ? String(value) : '')
  useEffect(() => { setVal(value != null ? String(value) : '') }, [value])
  if (!editing) {
    return <button onClick={() => setEditing(true)} className="hover:underline tabular-nums" title={title}>{value != null ? gbp(value) : <span className="text-txt-tertiary">{placeholder}</span>}</button>
  }
  const save = () => {
    const n = val.trim() === '' ? null : Number(val)
    if (n != null && (!Number.isFinite(n) || n < 0)) { crmToast('Enter a valid amount', 'error'); return }
    setEditing(false); onSave(n)
  }
  return (
    <input
      autoFocus value={val} onChange={(e) => setVal(e.target.value)} onBlur={save}
      onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false) }}
      className="w-24 px-1.5 py-0.5 rounded border border-border bg-surface text-right text-[12px] outline-none focus:border-brand-500"
    />
  )
}

// ── expense row + panel (Expenses tab) ───────────────────────────────────────
const ExpenseRow: React.FC<{ e: EventExpense; onChanged: () => void }> = ({ e, onChanged }) => {
  const [confirm, setConfirm] = useState(false)
  const [linkOpen, setLinkOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  const addLink = () => {
    const url = linkUrl.trim()
    if (!/^https?:\/\//i.test(url)) { crmToast('Enter a full link (https://…)', 'error'); return }
    addExpenseUrl(e.id, { url }).then(() => { setLinkUrl(''); setLinkOpen(false); onChanged() }).catch(() => crmToast('Could not add the link', 'error'))
  }
  const onFile = (f: File | undefined) => {
    if (!f) return
    if (f.size > 5 * 1024 * 1024) { crmToast('Max 5 MB per file', 'error'); return }
    uploadExpenseFile(e.id, f).then(() => { crmToast('Attached', 'success'); onChanged() }).catch(() => crmToast('Upload failed', 'error'))
  }

  return (
    <div className="rounded-lg border border-border bg-surface px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="text-[12px] font-medium text-txt-primary flex-1 truncate">{e.label}</span>
        {e.category && <Pill tone="gray">{e.category}</Pill>}
        {e.incurredAt && <span className="text-[10px] text-txt-tertiary tabular-nums shrink-0">{fmtDate(e.incurredAt)}</span>}
        <span className="text-[12px] font-semibold text-txt-primary tabular-nums shrink-0">{gbp(e.amount)}</span>
        <button onClick={() => setLinkOpen((o) => !o)} className="inline-flex text-txt-tertiary hover:text-txt-primary" title="Attach a link">
          <IconLink className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => fileRef.current?.click()} className="inline-flex text-txt-tertiary hover:text-txt-primary" title="Attach a file or image (receipt, invoice, photo)">
          <IconUpload className="w-3.5 h-3.5" />
        </button>
        <input ref={fileRef} type="file" className="hidden" onChange={(ev) => { onFile(ev.target.files?.[0]); ev.target.value = '' }} />
        <button onClick={() => setConfirm(true)} className="inline-flex text-txt-tertiary hover:text-red-600" title="Delete this expense">
          <IconTrash className="w-3.5 h-3.5" />
        </button>
      </div>
      {linkOpen && (
        <div className="mt-1.5 flex items-center gap-1.5">
          <input
            autoFocus value={linkUrl} onChange={(ev) => setLinkUrl(ev.target.value)}
            onKeyDown={(ev) => { if (ev.key === 'Enter') addLink(); if (ev.key === 'Escape') setLinkOpen(false) }}
            placeholder="https://…" className="flex-1 px-2 py-1 rounded border border-border bg-surface text-[11px] outline-none focus:border-brand-500"
          />
          <Button size="sm" onClick={addLink}>Add</Button>
        </div>
      )}
      {e.links.length > 0 && (
        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
          {e.links.map((l) => (
            <span key={l.id} className="inline-flex items-center gap-1 pl-1.5 pr-1 py-0.5 rounded-full border border-border text-[10px] text-txt-secondary max-w-[200px]">
              <a href={l.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-brand-600 min-w-0" title={l.name || l.url}>
                {l.kind === 'link' ? <IconLink className="w-3 h-3 shrink-0" /> : <IconPaperclip className="w-3 h-3 shrink-0" />}
                <span className="truncate">{l.name || l.url.replace(/^https?:\/\//, '')}</span>
              </a>
              <button
                onClick={() => deleteExpenseLink(l.id).then(onChanged).catch(() => crmToast('Could not remove', 'error'))}
                className="inline-flex text-txt-tertiary hover:text-red-600" title="Remove attachment"
              >✕</button>
            </span>
          ))}
        </div>
      )}
      {confirm && (
        <ConfirmDialog
          title="Delete this expense?"
          message={<>“{e.label}” ({gbp(e.amount)}) and its attachments will be removed. The event's totals update immediately.</>}
          confirmLabel="Delete"
          onConfirm={async () => { await deleteEventExpense(e.id); onChanged() }}
          onClose={() => setConfirm(false)}
        />
      )}
    </div>
  )
}

const CATEGORIES = ['Stand', 'Travel', 'Hotel', 'Staff', 'Marketing', 'Shipping', 'Other']

const ExpensesTab: React.FC<{ ev: PlannerEvent; onTotalsChanged: () => void }> = ({ ev, onTotalsChanged }) => {
  const [expenses, setExpenses] = useState<EventExpense[] | null>(null)
  const [label, setLabel] = useState('')
  const [amount, setAmount] = useState('')
  const [category, setCategory] = useState('')
  const load = () => listEventExpenses(ev.groupId).then(setExpenses).catch(() => setExpenses([]))
  useEffect(() => { setExpenses(null); load() }, [ev.groupId]) // eslint-disable-line react-hooks/exhaustive-deps

  const add = () => {
    const n = Number(amount)
    if (!label.trim()) { crmToast('Give the expense a name', 'error'); return }
    if (!Number.isFinite(n) || n < 0) { crmToast('Enter a valid amount', 'error'); return }
    addEventExpense(ev.groupId, { label: label.trim(), amount: n, category: category || null })
      .then(() => { setLabel(''); setAmount(''); setCategory(''); load(); onTotalsChanged() })
      .catch(() => crmToast('Could not add the expense', 'error'))
  }

  return (
    <div>
      {/* summary strip — always visible, consistent across tabs */}
      <div className="flex items-center gap-4 mb-3 text-[12px]">
        <span className="text-txt-secondary">Spent <span className={`font-semibold tabular-nums ${ev.budget != null && ev.spent > ev.budget ? 'text-red-600' : 'text-txt-primary'}`}>{gbp(ev.spent)}</span></span>
        <span className="text-txt-secondary">Expected <span className="font-semibold tabular-nums text-txt-primary">
          <InlineMoney value={ev.budget} placeholder="+ set" title="Expected cost (budget) — click to edit"
            onSave={(n) => updateOppGroup(ev.groupId, { budget: n }).then(() => { crmToast(n == null ? 'Expected cost cleared' : `Expected cost set to ${gbp(n)}`, 'success'); onTotalsChanged() }).catch(() => crmToast('Could not save', 'error'))} />
        </span></span>
        <span className="flex-1 max-w-[200px]"><SpendBar spent={ev.spent} budget={ev.budget} /></span>
      </div>
      <div className="flex items-center gap-1.5 mb-3">
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="What was it? e.g. Stand hire" className="flex-1 min-w-0 px-2.5 py-2 rounded-lg border border-border bg-surface text-[12px] outline-none focus:border-brand-500" />
        <input value={amount} onChange={(e) => setAmount(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add() }} placeholder="£" className="w-24 px-2.5 py-2 rounded-lg border border-border bg-surface text-[12px] text-right outline-none focus:border-brand-500" />
        <select value={category} onChange={(e) => setCategory(e.target.value)} className="px-2 py-2 rounded-lg border border-border bg-surface text-[11px] text-txt-secondary outline-none">
          <option value="">Category…</option>
          {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <Button size="sm" onClick={add}>Add</Button>
      </div>
      {!expenses ? (
        <p className="text-[12px] text-txt-tertiary">Loading…</p>
      ) : expenses.length === 0 ? (
        <p className="text-[12px] text-txt-tertiary">No expenses recorded yet — add the first above. Attach receipts, invoices or links to each line.</p>
      ) : (
        <div className="space-y-1.5">
          {expenses.map((e) => <ExpenseRow key={e.id} e={e} onChanged={() => { load(); onTotalsChanged() }} />)}
        </div>
      )}
    </div>
  )
}

// ── Leads tab ────────────────────────────────────────────────────────────────
const LeadsTab: React.FC<{ ev: PlannerEvent; onOpenLead: (dealId: string) => void }> = ({ ev, onOpenLead }) => {
  const [leads, setLeads] = useState<EventLeadValue[] | null>(null)
  useEffect(() => { setLeads(null); getEventLeadValues(ev.groupId).then(setLeads).catch(() => setLeads([])) }, [ev.groupId])
  return (
    <div>
      <div className="flex items-center gap-4 mb-3 text-[12px] text-txt-secondary flex-wrap">
        <span>Pipeline <span className="font-semibold tabular-nums text-txt-primary">{gbp(ev.estPipelineAnnual)}</span>/yr</span>
        <span>Won <span className="font-semibold tabular-nums text-emerald-700">{gbp(ev.actualAnnualValue)}</span>/yr</span>
        <span>{ev.uniqueLeads} lead{ev.uniqueLeads === 1 ? '' : 's'} · {ev.newCustomers} won</span>
        <span className="text-txt-tertiary text-[11px]">One row per company (duplicates merged) · won = live after event start, real MRR × 12 · others by size band · lost & pre-existing customers = £0</span>
      </div>
      {!leads ? (
        <p className="text-[12px] text-txt-tertiary">Loading…</p>
      ) : leads.length === 0 ? (
        <p className="text-[12px] text-txt-tertiary">No leads uploaded for this event yet — import them from Shows/Events and each arrives valued by company size and revenue.</p>
      ) : (
        <div className="space-y-0.5">
          {leads.map((l) => (
            <div
              key={l.oppId}
              onClick={() => { if (l.dealId) onOpenLead(l.dealId) }}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-[12px] ${l.dealId ? 'cursor-pointer hover:bg-surface-hover' : 'opacity-70'}`}
              title={l.dealId ? 'Open this lead' : 'Captured at the event but never made it into the CRM — no lead to open'}
            >
              {/* Avatar: WeQuote logo → uploaded image → initial placeholder (Lee, 7 Sep). */}
              <InitialBox name={l.company || l.name} size={24} logoUrl={l.avatarUrl || undefined} />
              <span className="flex-1 min-w-0 truncate text-txt-primary">{l.company || l.name}</span>
              {l.stage && <span className="text-[9px] uppercase tracking-wider text-txt-tertiary shrink-0">{STAGE_MAP[l.stage]?.label || l.stage.replace(/_/g, ' ')}</span>}
              {l.sizeBand && <span className="text-[10px] text-txt-tertiary tabular-nums shrink-0" title="Company size band (employees)">{l.sizeBand}</span>}
              {l.assumed && <span className="text-[10px] text-amber-600 shrink-0" title="No company size on record — valued at the small-band default">est*</span>}
              {l.preExisting ? (
                <span className="text-[10px] text-txt-tertiary shrink-0" title="Already a customer before this event started — the event didn't win them, so they count £0 here">existing customer · £0</span>
              ) : (
                <span className={`font-semibold tabular-nums shrink-0 ${l.isCustomer ? 'text-emerald-700' : l.estAnnual === 0 ? 'text-txt-tertiary line-through' : 'text-txt-primary'}`}
                  title={l.isCustomer ? 'Won by this event — real MRR × 12' : l.estAnnual === 0 ? 'Lost/churned — worth £0' : 'Estimated annual value'}>
                  {gbp(l.isCustomer ? (l.actualAnnual || 0) : l.estAnnual)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Overview tab ─────────────────────────────────────────────────────────────
const Stat: React.FC<{ label: string; value: React.ReactNode; hint?: string; tone?: string }> = ({ label, value, hint, tone }) => (
  <div className="rounded-lg border border-border bg-surface px-3 py-2.5" title={hint}>
    <div className="text-[9px] font-semibold uppercase tracking-wider text-txt-tertiary mb-0.5">{label}</div>
    <div className={`text-[15px] font-semibold tabular-nums ${tone || 'text-txt-primary'}`}>{value}</div>
  </div>
)

const OverviewTab: React.FC<{ ev: PlannerEvent; onSaved: () => void }> = ({ ev, onSaved }) => {
  const saveBudget = (n: number | null) =>
    updateOppGroup(ev.groupId, { budget: n }).then(() => { crmToast(n == null ? 'Expected cost cleared' : `Expected cost set to ${gbp(n)}`, 'success'); onSaved() }).catch(() => crmToast('Could not save', 'error'))
  // Broken even only when money has actually come in and covered the spend —
  // £0 earned against £0 spent is "nothing yet", not an achievement.
  const brokeEven = ev.earned > 0 && ev.earned >= ev.spent
  return (
    <div className="space-y-4">
      {/* Won stays its OWN number — pipeline is open leads only (Lee, 7 Sep). */}
      <div className="grid grid-cols-2 xl:grid-cols-3 gap-2">
        <Stat label="Pipeline (yr)" value={gbp(ev.estPipelineAnnual)} hint="Estimated annual value of leads still in play — customers excluded, they're Won" />
        <Stat label="Spent" value={gbp(ev.spent)} tone={ev.budget != null && ev.spent > ev.budget ? 'text-red-600' : undefined} hint={`${ev.expenseCount} expense line${ev.expenseCount === 1 ? '' : 's'}`} />
        <Stat label="Net" value={`${ev.net >= 0 ? '+' : '−'}${gbp(Math.abs(ev.net))}`} tone={netTone(ev.net)} hint="PROJECTED: pipeline + won − spent. For money actually collected, see Total Earned." />
        <Stat label="Leads captured" value={ev.uniqueLeads} hint={`Companies, deduped — several people from one company count once. ${ev.inCrm} reached the CRM.`} />
        <Stat label="Won (yr)" value={gbp(ev.actualAnnualValue)} tone={ev.actualAnnualValue > 0 ? 'text-emerald-700' : 'text-txt-tertiary'} hint={`${ev.newCustomers} customer${ev.newCustomers === 1 ? '' : 's'} the EVENT won (went live after its start date) — pre-existing customers excluded`} />
        <Stat label="Expected cost" value={<InlineMoney value={ev.budget} placeholder="+ set" onSave={saveBudget} title="Expected cost (budget) — click to edit" />} />
      </div>

      {/* Money actually in the bank — same-size cards as the row above (Lee, 7 Sep). */}
      <div className="grid grid-cols-2 xl:grid-cols-3 gap-2">
        <Stat label="Total Earned" value={gbp(ev.earned)} tone={ev.earned > 0 ? 'text-emerald-700' : 'text-txt-tertiary'}
          hint="Collected from companies that went live AFTER the event start, from this event's leads. Accrues monthly (months live × MRR from WeQuote's data)." />
        <Stat label="Left to Breakeven" value={gbp(ev.leftToBreakeven)} tone={brokeEven ? 'text-emerald-700' : ev.leftToBreakeven > 0 ? 'text-amber-600' : 'text-txt-tertiary'}
          hint="Spent minus earned — what the event still has to pay back. Moves as spend and payments change." />
        <Stat label="Profit over Breakeven" value={gbp(ev.profitOverBreakeven)} tone={ev.profitOverBreakeven > 0 ? 'text-emerald-700' : 'text-txt-tertiary'}
          hint="Everything earned beyond what was spent — only starts counting once the event has broken even." />
      </div>

      <div>
        <div className="text-[9px] font-semibold uppercase tracking-wider text-txt-tertiary mb-1.5">Funnel</div>
        <div className="flex items-center gap-1.5 flex-wrap text-[11px]">
          {[
            ['Captured', ev.uniqueLeads], ['In CRM', ev.inCrm], ['Demos', ev.demos],
            ['Trials', ev.trials], ['Customers', ev.newCustomers],
          ].map(([lab, n], i) => (
            <React.Fragment key={String(lab)}>
              {i > 0 && <span className="text-txt-tertiary">→</span>}
              <span className="px-2 py-0.5 rounded-full border border-border text-txt-secondary"><span className="font-semibold text-txt-primary tabular-nums">{n}</span> {lab}</span>
            </React.Fragment>
          ))}
          {ev.lost > 0 && <Pill tone="gray">{ev.lost} lost</Pill>}
          {ev.churned > 0 && <Pill tone="red">{ev.churned} churned</Pill>}
          {ev.notFollowedUp > 0 && <Pill tone="amber">{ev.notFollowedUp} never followed up</Pill>}
        </div>
      </div>
    </div>
  )
}

// ── left column card / row ───────────────────────────────────────────────────
const EventCard: React.FC<{ ev: PlannerEvent; selected: boolean; onSelect: () => void }> = ({ ev, selected, onSelect }) => {
  const t = oppGroupType(ev.type)
  return (
    <button
      onClick={onSelect}
      className={`w-full text-left rounded-xl border bg-surface p-3.5 transition-colors ${selected ? 'border-brand-500 ring-1 ring-brand-500/30' : 'border-border hover:border-txt-tertiary/40'}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-txt-primary truncate">{ev.name}</div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider" style={{ color: t.colour }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: t.colour }} />{t.label}
            </span>
            <DateRange ev={ev} />
          </div>
        </div>
        <div className={`text-[13px] font-bold tabular-nums shrink-0 ${netTone(ev.net)}`} title="Pipeline + won − spent">
          {ev.net >= 0 ? '+' : '−'}{gbp(Math.abs(ev.net))}
        </div>
      </div>
      <div className="mb-2"><SpendBar spent={ev.spent} budget={ev.budget} /></div>
      <div className="flex items-center gap-3 text-[10px] text-txt-tertiary tabular-nums">
        <span title="Companies captured (deduped)"><span className="font-semibold text-txt-secondary">{ev.uniqueLeads}</span> leads</span>
        <span title="Estimated annual pipeline — open leads only, Won separate"><span className="font-semibold text-txt-secondary">{gbp(ev.estPipelineAnnual)}</span> pipeline</span>
        <span title="Actual spend"><span className={`font-semibold ${ev.budget != null && ev.spent > ev.budget ? 'text-red-600' : 'text-txt-secondary'}`}>{gbp(ev.spent)}</span> spent</span>
        {ev.actualAnnualValue > 0 && <span className="text-emerald-700 font-semibold" title={`${ev.customers} paying customer(s) — annual value`}>{gbp(ev.actualAnnualValue)} won</span>}
      </div>
    </button>
  )
}

const EventRowItem: React.FC<{ ev: PlannerEvent; selected: boolean; onSelect: () => void }> = ({ ev, selected, onSelect }) => {
  const t = oppGroupType(ev.type)
  return (
    <button
      onClick={onSelect}
      className={`w-full text-left px-3 py-2 rounded-lg border transition-colors flex items-center gap-2.5 ${selected ? 'border-brand-500 bg-surface ring-1 ring-brand-500/30' : 'border-transparent hover:bg-surface-hover'}`}
    >
      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: t.colour }} title={t.label} />
      <span className="flex-1 min-w-0">
        <span className="block text-[12px] font-medium text-txt-primary truncate">{ev.name}</span>
        <span className="block text-[10px] text-txt-tertiary">{fmtDate(ev.date) || 'No date'} · {ev.uniqueLeads} leads · {gbp(ev.spent)} spent</span>
      </span>
      <span className={`text-[12px] font-semibold tabular-nums shrink-0 ${netTone(ev.net)}`}>{ev.net >= 0 ? '+' : '−'}{gbp(Math.abs(ev.net))}</span>
    </button>
  )
}

// ── add-event modal — writes the same table Shows/Events reads ───────────────
const AddEventModal: React.FC<{ onClose: () => void; onCreated: () => void }> = ({ onClose, onCreated }) => {
  const [name, setName] = useState('')
  const [type, setType] = useState('trade_show')
  const [date, setDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [location, setLocation] = useState('')
  const [budget, setBudget] = useState('')
  const [busy, setBusy] = useState(false)

  const save = () => {
    if (!name.trim()) { crmToast('Give the event a name', 'error'); return }
    const b = budget.trim() === '' ? null : Number(budget)
    if (b != null && (!Number.isFinite(b) || b < 0)) { crmToast('Enter a valid expected cost', 'error'); return }
    setBusy(true)
    createOppGroup({ name: name.trim(), type, date: date || undefined, endDate: endDate || null, budget: b, location: location.trim() || undefined })
      .then(() => { crmToast('Event created — it also appears on Shows/Events', 'success'); onCreated(); onClose() })
      .catch(() => crmToast('Could not create the event', 'error'))
      .finally(() => setBusy(false))
  }
  const field = 'w-full px-2.5 py-2 rounded-lg border border-border bg-surface text-sm outline-none focus:border-brand-500'
  return (
    <Overlay onClose={onClose} width={480}>
      <div className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-txt-primary">Add an event</h3>
          <CloseButton onClick={onClose} />
        </div>
        <div className="space-y-3">
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Event name, e.g. ISE 2027" className={field} />
          <div className="grid grid-cols-2 gap-3">
            <select value={type} onChange={(e) => setType(e.target.value)} className={field}>
              {OPP_GROUP_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Location" className={field} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-txt-tertiary">Starts</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={field} />
            </label>
            <label className="block">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-txt-tertiary">Ends</span>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={field} />
            </label>
          </div>
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-txt-tertiary">Expected cost (£) — optional</span>
            <input value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="e.g. 8000" className={field} />
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button loading={busy} onClick={save}>Create event</Button>
        </div>
      </div>
    </Overlay>
  )
}

// ── page: LEFT events column · RIGHT permanent tabbed detail panel ───────────
export const CrmEventPlanner: React.FC = () => {
  const [events, setEvents] = useState<PlannerEvent[] | null>(null)
  const [view, setView] = useState<'cards' | 'list'>('cards')
  const [tab, setTab] = useState<'overview' | 'expenses' | 'leads'>('overview')
  const [q, setQ] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [openDeal, setOpenDeal] = useState<any | null>(null)
  const openLead = (dealId: string) => getDealById(dealId).then(setOpenDeal).catch(() => crmToast('Could not open the lead', 'error'))

  const load = () => getPlannerEvents().then((r) => setEvents(r.events)).catch(() => crmToast('Could not load the planner', 'error'))
  useEffect(() => { load() }, [])

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const list = events || []
    return needle ? list.filter((e) => `${e.name} ${e.type} ${e.location || ''}`.toLowerCase().includes(needle)) : list
  }, [events, q])

  // Keep a valid selection: default to the first visible event, follow reloads.
  useEffect(() => {
    if (!shown.length) { setSelectedId(null); return }
    if (!selectedId || !shown.some((e) => e.groupId === selectedId)) setSelectedId(shown[0].groupId)
  }, [shown]) // eslint-disable-line react-hooks/exhaustive-deps

  const selected = shown.find((e) => e.groupId === selectedId) || null

  const totals = useMemo(() => {
    const list = events || []
    return {
      pipeline: list.reduce((s, e) => s + e.estPipelineAnnual, 0),
      won: list.reduce((s, e) => s + e.actualAnnualValue, 0),
      spent: list.reduce((s, e) => s + e.spent, 0),
      net: list.reduce((s, e) => s + e.net, 0),
      leads: list.reduce((s, e) => s + e.uniqueLeads, 0),
      earned: list.reduce((s, e) => s + e.earned, 0),
    }
  }, [events])

  const selType = selected ? oppGroupType(selected.type) : null

  return (
    <div className="h-full flex flex-col bg-surface-secondary">
      <CrmToaster />
      {/* header */}
      <div className="px-6 pt-5 pb-3 bg-surface border-b border-border shrink-0">
        <div className="flex items-end justify-between gap-4 flex-wrap mb-3">
          <div>
            <h1 className="text-lg font-semibold text-txt-primary">Event Planner</h1>
          </div>
          <div className="flex items-center gap-2">
            <SearchInput value={q} onChange={setQ} placeholder="Search events…" compact />
            <SegTabs size="sm" tabs={[{ key: 'cards', label: 'Cards' }, { key: 'list', label: 'List' }]} value={view} onChange={(k) => setView(k as any)} />
            <Button size="sm" onClick={() => setAdding(true)}>Add event</Button>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Kpi label="Events" value={events ? events.length : '—'} />
          <Kpi label="Pipeline (yr)" value={gbp(totals.pipeline)} tone="blue" />
          <Kpi label="Won (yr)" value={gbp(totals.won)} tone="green" />
          <Kpi label="Earned" value={gbp(totals.earned)} tone="green" />
          <Kpi label="Spent" value={gbp(totals.spent)} tone={totals.spent > totals.pipeline + totals.won ? 'red' : 'default'} />
          <Kpi label="Net" value={`${totals.net >= 0 ? '+' : '−'}${gbp(Math.abs(totals.net))}`} tone={totals.net >= 0 ? 'green' : 'red'} />
          <Kpi label="Leads" value={totals.leads} />
        </div>
      </div>

      {/* split body */}
      <div className="flex-1 min-h-0 flex">
        {/* LEFT — every event, cards or rows */}
        <div className="w-[360px] xl:w-[400px] shrink-0 border-r border-border overflow-y-auto p-4">
          {!events ? (
            <p className="text-sm text-txt-tertiary">Loading…</p>
          ) : shown.length === 0 ? (
            <p className="text-sm text-txt-tertiary">{q ? 'No events match the search.' : 'No events yet — add the first one above.'}</p>
          ) : view === 'cards' ? (
            <div className="space-y-2.5">
              {shown.map((ev) => <EventCard key={ev.groupId} ev={ev} selected={ev.groupId === selectedId} onSelect={() => setSelectedId(ev.groupId)} />)}
            </div>
          ) : (
            <div className="space-y-1">
              {shown.map((ev) => <EventRowItem key={ev.groupId} ev={ev} selected={ev.groupId === selectedId} onSelect={() => setSelectedId(ev.groupId)} />)}
            </div>
          )}
        </div>

        {/* RIGHT — one permanent detail panel, tabbed */}
        <div className="flex-1 min-w-0 flex flex-col bg-surface">
          {!selected ? (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-sm text-txt-tertiary">Select an event on the left to see its numbers, expenses and lead values.</p>
            </div>
          ) : (
            <>
              <div className="px-6 pt-4 pb-3 border-b border-border shrink-0">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <h2 className="text-[15px] font-semibold text-txt-primary truncate">{selected.name}</h2>
                    {selType && (
                      <span className="inline-flex items-center gap-1 mt-1 text-[10px] font-semibold uppercase tracking-wider" style={{ color: selType.colour }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: selType.colour }} />{selType.label}
                      </span>
                    )}
                  </div>
                  <div className={`text-lg font-bold tabular-nums shrink-0 ${netTone(selected.net)}`} title="Pipeline + won − spent">
                    {selected.net >= 0 ? '+' : '−'}{gbp(Math.abs(selected.net))}
                  </div>
                </div>
                <div className="mt-3">
                  <SegTabs
                    size="sm"
                    tabs={[
                      { key: 'overview', label: 'Overview' },
                      { key: 'expenses', label: `Expenses${selected.expenseCount ? ` (${selected.expenseCount})` : ''}` },
                      { key: 'leads', label: `Leads${selected.uniqueLeads ? ` (${selected.uniqueLeads})` : ''}` },
                    ]}
                    value={tab}
                    onChange={(k) => setTab(k as any)}
                  />
                </div>
                {/* Main details — right below the tabs, evident, editable (Lee, 7 Sep). */}
                <div className="mt-3 flex items-center gap-5 flex-wrap text-[13px] text-txt-primary font-medium">
                  <label className="inline-flex items-center gap-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-txt-secondary">Starts</span>
                    <input
                      type="date" key={`s-${selected.groupId}`} defaultValue={ymdOf(selected.date)}
                      onChange={(e) => updateOppGroup(selected.groupId, { date: e.target.value || undefined }).then(() => { crmToast('Start date saved', 'success'); load() }).catch(() => crmToast('Could not save', 'error'))}
                      className="px-2 py-1 rounded-lg border border-border bg-surface text-[13px] font-medium text-txt-primary outline-none focus:border-brand-500"
                    />
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-txt-secondary">Ends</span>
                    <input
                      type="date" key={`e-${selected.groupId}`} defaultValue={ymdOf(selected.endDate)}
                      onChange={(e) => updateOppGroup(selected.groupId, { endDate: e.target.value || null }).then(() => { crmToast('End date saved', 'success'); load() }).catch(() => crmToast('Could not save', 'error'))}
                      className="px-2 py-1 rounded-lg border border-border bg-surface text-[13px] font-medium text-txt-primary outline-none focus:border-brand-500"
                    />
                  </label>
                  <span className="inline-flex items-center gap-2 min-w-0">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-txt-secondary">Location</span>
                    <input
                      type="text" key={`l-${selected.groupId}`} defaultValue={selected.location || ''} placeholder="Add location"
                      onBlur={(e) => { const v = e.target.value.trim(); if (v !== (selected.location || '')) updateOppGroup(selected.groupId, { location: v }).then(() => { crmToast('Location saved', 'success'); load() }).catch(() => crmToast('Could not save', 'error')) }}
                      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur() }}
                      className="px-2 py-1 rounded-lg border border-border bg-surface text-[13px] font-medium text-txt-primary outline-none focus:border-brand-500 w-44"
                    />
                  </span>
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
                {tab === 'overview' && <OverviewTab ev={selected} onSaved={load} />}
                {tab === 'expenses' && <ExpensesTab ev={selected} onTotalsChanged={load} />}
                {tab === 'leads' && <LeadsTab ev={selected} onOpenLead={openLead} />}
              </div>
            </>
          )}
        </div>
      </div>

      {adding && <AddEventModal onClose={() => setAdding(false)} onCreated={load} />}
      {/* Full lead view straight from the planner (Lee, 7 Sep 2026). */}
      {openDeal && <LeadRedirect deal={openDeal} onDone={() => setOpenDeal(null)} />}
    </div>
  )
}
